<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue';
import { PrimaryButton, QuestionEmail, LocationSelector } from '@hfc/ui-core';
import { ChallengeQuestionFields } from '@/application/shared/domain/ChallengeQuestionSettings';
import { APPLICATION_TYPES } from '@hfc/common';
import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';
import DateSelector from '@/shared/infrastructure/components/DateSelector.vue';
import { useForm, useField, Field } from 'vee-validate';
import { MaskaDetail, MaskInputOptions } from 'maska';
import { vMaska } from 'maska/vue';
import GoogleRecaptcha from '@/shared/infrastructure/components/GoogleRecaptcha.vue';
import { ChallengeQuestion } from '@/application/shared/domain/ChallengeQuestion';

interface EmitInterface {
  (event: 'submit', params: ChallengeQuestion): void,
  (event: 'back'): void
}

const emits = defineEmits<EmitInterface>();

const props = withDefaults(defineProps<{
  formConfig: Partial<ChallengeQuestionFields>;
  applicationType: APPLICATION_TYPES;
  buttonText?: string;
  loading: boolean;
}>(), {
  buttonText: 'Submit'
});

const { handleSubmit } = useForm();
const runValidations = handleSubmit(() => true);
const maskObject = reactive({} as MaskaDetail);
const maskOptions: MaskInputOptions = {
  onMaska: (detail: MaskaDetail): void => {
    Object.assign(maskObject, detail);
  }
};

const dateOfBirth = ref();
const lastFourSSN = ref(undefined);
const email = ref();
const { value: zipCode } = useField('zipCode', { required: props.formConfig.zip_code }, { initialValue: undefined as number | undefined });

const recaptchaRef = ref<InstanceType<typeof GoogleRecaptcha> | null>(null);
const recaptchaToken = ref('');

const onSubmit = (): void => {
  emits('submit', {
    zip_code: zipCode.value,
    date_of_birth: dateOfBirth.value,
    ssn: lastFourSSN.value,
    email: email.value,
  });
};

const changeEmailValue = (value: string): void => {
  email.value = value;
};

const onCaptchaSuccess = (token: string | null): void => {
  if (token) {
    recaptchaToken.value = token;
    onSubmit();
  }
};

const beforeSubmit = async (): Promise<void> => {
  const valid = await runValidations();
  if (!valid) {
    return;
  }
  recaptchaRef.value?.executeRecaptcha();
};

onMounted(() => {
  recaptchaRef.value?.renderRecaptcha();
});
</script>
<template>
  <BaseSection>
    <form @submit.prevent="beforeSubmit" @keydown.enter.prevent="beforeSubmit" class="flex flex-col min-h-[180px]">
      <div class="flex flex-col sm:flex-row justify-center sm:space-x-16">
        <LocationSelector v-if="formConfig.zip_code" custom-zip-class="w-72 h-16" :hide-county="true"
          :validate-coverage="false" :fetch-counties="false" :zipcode="zipCode" zip-size="" :application-type="applicationType"
          @update:zip-code="zipCode = $event"
          />
        <div v-if="formConfig.date_of_birth && (!formConfig.ssn || !formConfig.zip_code)" class="sm:w-72">
          <Field v-model="dateOfBirth" v-slot="{ field, errorMessage, meta }" name="challenge-dob"
            :rules="{ required: true, not_one_of: [0] }">
            <DateSelector label="Birthday" id="challenge-dob" v-bind:model-value="field.value"
              @update:modelValue="val => dateOfBirth = val" :has-errors="!meta.valid && meta.validated" />
            <span v-if="errorMessage" class="error">{{ errorMessage }}</span>
          </Field>
        </div>
        <div v-if="formConfig.ssn" class="sm:w-72">
          <div class="flex flex-col relative">
            <label class="font-medium mb-1 text-sky-700 block">
              Last four of SSN
            </label>
            <Field v-model="lastFourSSN" v-slot="{ field, errorMessage, meta }" name="challenge-ssn"
              :rules="{ required: true }">
              <div class="flex items-center">
                <input class="h-16 border-2 text-lg border-blue-100 w-full px-4 py-2 outline-0" type="text" inputmode="numeric"
                  placeholder="Last four digits" data-maska="####" data-maska-eager v-maska="maskOptions" v-bind="field"
                  :class="[meta]" v-no-arrow-keys />
              </div>
              <span v-if="errorMessage" class="error">{{ errorMessage }}</span>
            </Field>
          </div>
        </div>
        <div v-if="formConfig.email" class="sm:w-72 mt-2 lg:mt-0">
          <div class="flex flex-col relative">
            <label class="font-medium mb-1 text-sky-700 block">
              Email
            </label>
            <QuestionEmail id="mainEmail" name="email" :use-form-context="true" custom-class="h-16 border-2 text-lg" :value="email" @update:value="(value: string) => changeEmailValue(value)" />
          </div>
        </div>
      </div>
    </form>
    <GoogleRecaptcha container-id="recaptcha-container-challenge-form" @captcha-success="onCaptchaSuccess" ref="recaptchaRef" />
  </BaseSection>
  <div class="flex justify-center mt-10">
    <PrimaryButton @click="beforeSubmit" :square="false" size="xl" :is-loading="loading" :disabled="loading">
      {{ buttonText }}
    </PrimaryButton>
  </div>
</template>
